"use client";

import { heroStyles, heroBodyStyles, heroContentStyles, heroActionsStyles, heroHeadlineStyles } from "./hero.styles";

import type { HeroProps } from "./hero.types";

import { Headline } from "../headline";
import { Body } from "../body";
import { Button, type ButtonProps } from "../button";

export const Hero: React.FC<HeroProps> = (props) => {
  const {
    className,
    headline,
    level = 2,
    layout = "center",
    body,
    actions,
    headlineSize = "2",
    headlineWeight,
    bodySize = "hero",
    ...rest
  } = props;

  const classes = heroStyles({ className, layout });

  const renderBody = () =>
    typeof body === "string" ? (
      <Body size={bodySize} className={heroBodyStyles({ layout })}>
        {body}
      </Body>
    ) : (
      <span className={heroBodyStyles({ layout })}>{body}</span>
    );

  const renderActions = () =>
    actions?.length ? (
      <div className={heroActionsStyles({ layout })}>
        {actions.map((action, index) => (
          // The `action` items are a union that includes a polymorphic `as` component
          // variant; Button itself is polymorphic so this spread is safe at runtime.
          // Cast through `unknown` to a concrete ButtonProps variant (without `ref`,
          // since React's LegacyRef on intrinsic element props is not assignable to
          // Button's forwarded `ref`) to collapse the union for TS.
          <Button key={`${action.label}-${index}`} {...(action as unknown as Omit<ButtonProps<"button">, "ref">)}>
            {action.label}
          </Button>
        ))}
      </div>
    ) : null;

  if (layout === "split") {
    return (
      <div className={classes} {...rest}>
        <div className={heroHeadlineStyles({ layout })}>
          <Headline size={headlineSize} as={`h${level}`} weight={headlineWeight}>
            {headline}
          </Headline>
        </div>

        <div className={heroContentStyles({ layout })}>
          {renderBody()}
          {renderActions()}
        </div>
      </div>
    );
  }

  return (
    <div className={classes} {...rest}>
      <div className={heroContentStyles({ layout })}>
        <Headline size={headlineSize} as={`h${level}`} weight={headlineWeight}>
          {headline}
        </Headline>
        {renderBody()}
      </div>
      {renderActions()}
    </div>
  );
};
